import { useMemo } from 'react';
import { FilterOption } from 'models/Filters';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import utils, { getSortBy, getIsDesc, getHasElementsInArray } from 'utils';
import { MULTI_SORT_SEPARATOR, SORT_PARAM_KEYWORD } from 'data/global';
import useQueryParams from 'hooks/useQueryParams';

export type FilterPill = {
  key: string;
  label: string;
  displayValue: string | null;
  onRemove: () => void;
};

export type SortPill = {
  key: string;
  label: string;
  isDesc: boolean;
  onRemove: () => void;
};

const parseParamValues = (rawValue?: string): string[] => {
  if (!rawValue) return [];
  const asArray = utils.mapParamsToArray(rawValue);
  return getHasElementsInArray(asArray) ? asArray : [rawValue];
};

const useActiveFilterSortPills = (
  filterOptions: FilterOption[],
  sortOptions: OptionValue<string>[],
) => {
  const { params, removeQueryParam, removeSeveralQueryParams, setQueryParam } =
    useQueryParams<Record<string, string>>();

  const sort = params[SORT_PARAM_KEYWORD];

  const filterPills = useMemo<FilterPill[]>(
    () =>
      filterOptions.reduce<FilterPill[]>((acc, option) => {
        const rawIdValue = params[option.value];
        if (!rawIdValue) return acc;

        const isSearchable = !('options' in option);
        const displayParamKey = isSearchable
          ? option.clearFields?.[0] ?? option.value
          : option.value;
        const removeKeys = [option.value, ...(option.clearFields ?? [])];

        const displayValues = parseParamValues(
          params[displayParamKey] ?? rawIdValue,
        );

        let displayValue: string | null = null;

        if (displayValues.length === 1) {
          if ('options' in option) {
            const match = option.options.find(
              ({ value }) => value === displayValues[0],
            );
            displayValue = match?.label ?? displayValues[0];
          } else {
            displayValue = displayValues[0];
          }
        } else if (displayValues.length > 1) {
          displayValue = String(displayValues.length);
        }

        acc.push({
          key: option.value,
          label: option.label,
          displayValue,
          onRemove: () => removeSeveralQueryParams(removeKeys),
        });

        return acc;
      }, []),
    [filterOptions, params, removeSeveralQueryParams],
  );

  const sortPills = useMemo<SortPill[]>(() => {
    if (!sort) return [];

    return sort
      .split(MULTI_SORT_SEPARATOR)
      .filter(Boolean)
      .map((entry) => {
        const field = getSortBy(entry);
        const isDesc = getIsDesc(entry);
        const label = String(
          sortOptions.find(({ value }) => value === field)?.label ?? field,
        );

        return {
          key: `sort-${field}`,
          label,
          isDesc,
          onRemove: () => {
            const remaining = sort
              .split(MULTI_SORT_SEPARATOR)
              .filter((e) => getSortBy(e) !== field)
              .join(MULTI_SORT_SEPARATOR)
              .trim();

            if (remaining) {
              setQueryParam(SORT_PARAM_KEYWORD, remaining);
            } else {
              removeQueryParam(SORT_PARAM_KEYWORD);
            }
          },
        };
      });
  }, [sort, sortOptions, setQueryParam, removeQueryParam]);

  const hasPills =
    getHasElementsInArray(sortPills) || getHasElementsInArray(filterPills);

  return { filterPills, sortPills, hasPills };
};

export default useActiveFilterSortPills;
